import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {Store} from '@ngrx/store';
import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';

import {OphAddButtonComponent} from '../../shared/design/oph-add-button/oph-add-button.component';
import {WikiItemComponent} from '../wiki-item/wiki-item.component';
import {WikiGroup, WikiItem, WikiItemOrder} from '../models/wiki.model';
import {WIKI_GROUP_DIALOG_SETTINGS} from '../shared/wiki.constants';
import {UpdateWikiGroupsOrderAction, UpdateWikiItemOrderAction} from './../../core/store/wiki/wiki.action';
import {WikiGroupDialogComponent, WikiGroupDialogData} from '../wiki-group-dialog/wiki-group-dialog.component';
import {MessageService} from './../../services/message.service';
import {OphIconModule} from './../../shared/design/oph-icon/oph-icon.module';
import {OphButtonModule} from './../../shared/design/oph-button/oph-button.module';
import {WikiItemDetailComponent} from '../wiki-item-detail/wiki-item-detail.component';
import {OphDrawerService} from './../../shared/design/oph-drawer/oph-drawer.service';
import {MobileService} from 'src/app/core/page/mobile.service';

@Component({
  selector: 'wiki-list',
  standalone: true,
  imports: [
    CommonModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
    CdkDragPreview,
    OphAddButtonComponent,
    MatIconModule,
    OphButtonModule,
    OphIconModule,
    WikiItemComponent,
  ],
  templateUrl: './wiki-list.component.html',
  styleUrl: './wiki-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WikiListComponent {
  private readonly mobileService = inject(MobileService);
  readonly dialog = inject(MatDialog);
  private store$ = inject(Store);
  private messageService = inject(MessageService);
  private drawerService = inject(OphDrawerService);

  @Input() randomColors = [];
  @Input() wikiGroups: WikiGroup[] = [];
  dragDisabled = true;
  hoveringIndex: number | null = null;

  getWikiGroupIds(): string[] {
    return this.wikiGroups.map(group => group._id);
  }

  dropWikiGroup(event: CdkDragDrop<WikiGroup[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.wikiGroups, event.previousIndex, event.currentIndex);
      const wikiGroupId = this.wikiGroups[event.currentIndex]._id;
      const wikiGroupNewOrder = event.currentIndex;

      this.store$.dispatch(
        new UpdateWikiGroupsOrderAction({
          wikiGroupOrder: {
            groupId: wikiGroupId,
            newOrder: wikiGroupNewOrder,
          },
          onSuccess: () => {
            this.messageService.add(`Group's order updated successfully`, 'newTaskAssigned');
          },
          onFailure: (error: string) => {
            this.messageService.add(`Failed to update group's order`, 'error');
          },
        })
      );
    }
  }

  dropWikiItem(event: CdkDragDrop<WikiItem[]>): void {
    const groupId = event.container.id;
    const itemId = event.item.element.nativeElement.id;
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.updateWikiItemsOrder({groupId, itemId, newOrder: event.currentIndex});
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
      this.updateWikiItemsOrder({groupId, itemId, newOrder: event.currentIndex});
    }
  }

  private updateWikiItemsOrder(wikiItemOrder: WikiItemOrder): void {
    this.store$.dispatch(
      new UpdateWikiItemOrderAction({
        wikiItemOrder: wikiItemOrder,
        onSuccess: () => {
          this.messageService.add(`Posts order updated successfully`, 'newTaskAssigned');
        },
        onFailure: (error: string) => {
          this.messageService.add(`Failed to update orde`, 'error');
        },
      })
    );
  }

  onCreateWikiGroup(): void {
    this.dialog.open(WikiGroupDialogComponent, {
      data: {group: null, groups: this.wikiGroups} as WikiGroupDialogData,
      ...WIKI_GROUP_DIALOG_SETTINGS,
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
    });
  }

  onEditWikiGroup(wikiGroup: WikiGroup): void {
    this.dialog.open(WikiGroupDialogComponent, {
      data: {group: wikiGroup, groups: this.wikiGroups} as WikiGroupDialogData,
      ...WIKI_GROUP_DIALOG_SETTINGS,
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
    });
  }

  onCreateWikiItem(wikiGroup: WikiGroup): void {
    this.drawerService.setDrawerContext({hideCloseButton: true, hideBackdrop: true, hideScrollbar: true});
    this.drawerService.loadComponent(WikiItemDetailComponent, {data: {item: null, group: wikiGroup}});
    this.drawerService.openDrawer();
  }

  onEditWikiItem(wikiItem: WikiItem): void {
    this.drawerService.setDrawerContext({hideCloseButton: true, hideBackdrop: true, hideScrollbar: true});
    this.drawerService.loadComponent(WikiItemDetailComponent, {data: {item: wikiItem}});
    this.drawerService.openDrawer();
  }
}
